/**
 * AppShell — the authenticated chrome around every funnel route.
 *
 * Deliberately minimal: a thin header and an <Outlet>. The workspace's own
 * left rail lives in `pages/listiclebuilders/ListicleBuildersLayout`, so this
 * shell only owns the things that must exist above every route — branding
 * slot, the signed-in user, and sign-out.
 *
 * Replace the wordmark below with your own. Nothing else in the package
 * renders a brand name.
 */
import React from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Radio } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const PRODUCT_NAME = "Funnel OS";
const HOME = "/app/production/listicle-builder";

// Funnel OS opens from the NextEcom dashboard's Funnels item as its own
// page (one Cloudflare Access session covers both hostnames); this is the way
// back. Overridable at build time for another dashboard host.
const DASHBOARD_URL = process.env.REACT_APP_DASHBOARD_URL || "https://crm.nextecomos.com/";

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen flex-col bg-background text-foreground" data-testid="app-shell">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4">
        <a href={DASHBOARD_URL} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-2xs text-muted-foreground hover:bg-secondary hover:text-foreground" data-testid="app-dashboard-link" title="Back to NextEcom OS">
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
          NextEcom OS
        </a>
        <Link to={HOME} className="flex items-center gap-2 font-medium tracking-tight" data-testid="app-home-link">
          <Radio className="h-4 w-4 text-[hsl(var(--slash-green))]" strokeWidth={1.75} />
          <span className="text-sm">{PRODUCT_NAME}</span>
        </Link>

        <nav className="ml-4 flex items-center gap-1 text-13">
          <Link to={HOME} className="rounded-md px-2.5 py-1.5 text-foreground/80 hover:bg-secondary hover:text-foreground">
            Dashboard
          </Link>
          <Link to={`${HOME}/websites`} className="rounded-md px-2.5 py-1.5 text-foreground/80 hover:bg-secondary hover:text-foreground">
            Funnels
          </Link>
          <Link to={`${HOME}/orders`} className="rounded-md px-2.5 py-1.5 text-foreground/80 hover:bg-secondary hover:text-foreground">
            Orders
          </Link>
          <Link to={`${HOME}/analytics`} className="rounded-md px-2.5 py-1.5 text-foreground/80 hover:bg-secondary hover:text-foreground">
            Analytics
          </Link>
          <Link to={`${HOME}/live`} className="rounded-md px-2.5 py-1.5 text-foreground/80 hover:bg-secondary hover:text-foreground">
            Live
          </Link>
          <Link to="/app/retention" className="rounded-md px-2.5 py-1.5 text-foreground/80 hover:bg-secondary hover:text-foreground">
            Retention
          </Link>
          <Link to="/app/settings/customer-portal" className="rounded-md px-2.5 py-1.5 text-foreground/80 hover:bg-secondary hover:text-foreground">
            Customer portal
          </Link>
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {user ? (
            <span className="text-2xs text-muted-foreground" data-testid="app-user">
              {user.email}
            </span>
          ) : null}
          <button
            type="button"
            onClick={signOut}
            data-testid="app-signout"
            className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-2xs text-foreground/80 hover:bg-secondary hover:text-foreground"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto" data-testid="app-main">
        <Outlet />
      </main>
    </div>
  );
}
